/**
 * The fill-gaps routes (#163): three candidate fills of one week's open shifts, and saving the one the
 * manager picks as drafts. Both answer 404 unless the server runs with PLANNING_FILL=on.
 */
import { post } from '@/api/client';

export interface PlanFields {
  user_id: number | null;
  shift_type_id: number | null;
  start_time: string;
  end_time: string;
  description: string | null;
}
export interface Op {
  op_id: string;
  kind: 'update' | 'create' | 'delete';
  target: { shift_id?: number; client_id?: string };
  changed: string[];
  before: PlanFields | null;
  after: PlanFields | null;
}
export interface Cost {
  visibility: 'visible' | 'restricted';
  status?: string;
  total_minor?: number | null;
  hours: number | null;
}
export interface Finding {
  finding_id: string;
  code: string;
  severity: 'danger' | 'warning';
  source: 'validator' | 'eligibility';
  in_period: boolean;
  /** false when the week as saved already says this, with the same facts (owner 18.10); absent from older servers */
  new?: boolean;
  user_id: number | null;
  date: string;
  measured: Record<string, string | number>;
  text: string;
  /** the finding's facts (code, shift, plan fields, measurement): an acknowledgement is bound to it */
  fingerprint: string;
}
export type Objective = 'lower_cost' | 'history_similarity' | 'weekend_balance';
export interface Proposal {
  proposal_id: string;
  revision: string;
  objective: Objective;
  origin: { kind: string; candidate_id: string };
  operations: Op[];
  coverage: { required: number; covered: number; uncovered: string[] };
  cost: Cost;
  /** hours each person is given in the after-state (contract v1) */
  hours_by_person?: { user_id: number; hours: number; weekend_shifts: number }[];
  findings: { status: string; items: Finding[]; not_checked?: { reason: string }[] };
  outcome: { status: string; reason?: string; uncovered?: string[] };
  input_completeness: { complete: boolean; missing: { input: string; ref: Record<string, unknown> }[] };
}
export interface BaseShift extends PlanFields {
  shift_id: number;
  published: boolean;
}
/** One line of a basis: a weekday's slots of one time and type. */
export interface BasisLine {
  date: string;
  from: string;
  to: string;
  shift_type_id: number | null;
  type_name: string | null;
  count: number;
}
export interface LeftOut {
  weekday?: number;
  from?: string;
  to?: string;
  type_name?: string | null;
  weeks?: number;
  /** need: how many people the hour still lacks */
  people?: number;
  key?: string;
  reason: string;
}
/** What an empty week can be planned from (contract revision 5). */
export interface Bases {
  /** the company's staffing need as shifts of its own types (owner 18.10); absent from older servers */
  need?: { slots: number; lines: BasisLine[]; left_out: LeftOut[] };
  templates: { template_id: number; name: string; slots: number; lines: BasisLine[]; left_out: LeftOut[] }[];
  history: {
    from: string;
    to: string;
    weeks: number;
    recur: number;
    slots: number;
    lines: BasisLine[];
    left_out: LeftOut[];
  };
  /** the most recent of the four weeks before that has shifts, every shift as a slot (owner 18.10) */
  week?: { week_start: string | null; slots: number; lines: BasisLine[] };
}
export type BasisRef =
  | { kind: 'template'; template_id: number }
  | { kind: 'history' }
  | { kind: 'week' }
  | { kind: 'need' };
/** The limits a manager may lift for a proposal (owner 18.10): contract hours and availability. */
export type Lift = 'contract' | 'availability';
/** Per candidate, why its open shifts are open: what would let somebody take each. */
export interface Limits {
  contract: number;
  availability: number;
  contract_and_availability: number;
  law: number;
}
export interface Preview {
  limits?: Record<string, Limits | null>;
  lift?: Lift[];
  /** the week's demand in hours against what the people may still work (owner 18.10) */
  capacity?: {
    need_hours: number;
    free_hours: number;
    people: { user_id: number; max_hours: number; free_hours: number }[];
  };
  /** an empty week: nothing is proposed until a basis is confirmed */
  basis_required?: boolean;
  bases?: Bases;
  /** the confirmed basis this answer was planned from */
  basis?: {
    kind: 'template' | 'history' | 'week' | 'need';
    ref: Record<string, unknown> & { name?: string };
    slots: number;
    lines: BasisLine[];
  };
  context: {
    people: { user_id: number; name: string }[];
    demand: { items: { demand_id: string; source: string }[] };
  };
  baseline: { shifts: BaseShift[] };
  baseline_cost: Cost;
  candidates: { candidate_id: string; duplicate_of: string | null }[];
  proposals: Proposal[];
}
/** A manager's change inside a proposal (P08): person and same-day times of one of its shifts. */
export interface Edit {
  target: { shift_id?: number; client_id?: string };
  user_id: number | null;
  start_time: string;
  end_time: string;
}
/** An acknowledged finding, bound to its exact facts (contract revision 6). */
export interface Ack {
  finding_id: string;
  fingerprint: string;
  reason: string | null;
}
export interface Applied {
  status: 'applied' | 'replayed';
  applied: { op_id: string; kind: string; shift_id: number }[];
  published: number;
}

export const previewFill = (
  week_start: string,
  team_id: number | null,
  basis: BasisRef | null = null,
  lift: Lift[] = [],
) =>
  post<Preview>('/admin/planning/fill-gaps/preview', {
    week_start,
    team_id,
    ...(basis ? { basis } : {}),
    ...(lift.length ? { lift } : {}),
  });

export const applyFill = (body: {
  week_start: string;
  team_id: number | null;
  candidate_id: string;
  proposal_revision: string;
  request_key: string;
  basis?: BasisRef;
  lift?: Lift[];
  edits?: Edit[];
  acknowledgements?: Ack[];
}) => post<Applied>('/admin/planning/fill-gaps/apply', body);

export const reviseFill = (body: {
  week_start: string;
  team_id: number | null;
  candidate_id: string;
  proposal_revision: string;
  edits: Edit[];
  basis?: BasisRef;
  lift?: Lift[];
}) => post<{ proposal: Proposal }>('/admin/planning/fill-gaps/revise', body);
